// ─── Workshop Truck Availability Tracker ───
// Dashboard view – Central command dashboard with KPIs, map, charts, tables & alerts
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useMemo } from 'react'
import { format } from 'date-fns'
import {
  Truck as TruckIcon,
  Wrench,
  CheckCircle,
  Flag,
  Activity,
  Eye,
  Edit2,
  Bell,
  MapPin,
} from 'lucide-react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

import type {
  DashboardData,
  AvailabilitySnapshot,
  Truck,
  ToastMessage,
} from '@/types'
import KPICard from '@/components/ui/KPICard'
import SkeletonCard from '@/components/ui/SkeletonCard'
import StatusBadge from '@/components/ui/StatusBadge'
import TruckDetailModal from '@/components/modals/TruckDetailModal'
import StatusUpdateModal from '@/components/modals/StatusUpdateModal'
import VehicleMap from '@/components/map/VehicleMap'
import { useTrucks } from '@/hooks/useTrucks'
import {
  STATUS_COLORS,
  calcDaysInWorkshop,
  getDaysColor,
  getAvailabilityColor,
  formatDate,
  truncate,
  normalizeMake,
} from '@/utils/helpers'
import type { WorkshopStatus } from '@/types'

// ─── Props ──────────────────────────────────────────────────

interface DashboardProps {
  data: DashboardData | null
  historyData: AvailabilitySnapshot[]
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
  addToast: (type: ToastMessage['type'], message: string) => void
}

// ─── Component ──────────────────────────────────────────────

export default function Dashboard({
  data,
  historyData,
  isLoading,
  error,
  refresh,
  addToast,
}: DashboardProps) {
  // ── Fetch all trucks for the map ────────────────────────────
  const { trucks: allTrucks } = useTrucks()

  // ── Modal state ───────────────────────────────────────────
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null)
  const [modalMode, setModalMode] = useState<'detail' | 'edit' | null>(null)

  const openDetail = (truck: Truck) => {
    setSelectedTruck(truck)
    setModalMode('detail')
  }
  const openEdit = (truck: Truck) => {
    setSelectedTruck(truck)
    setModalMode('edit')
  }
  const closeModal = () => {
    setSelectedTruck(null)
    setModalMode(null)
  }

  // ── Per-client Moving / Stopped / Workshop counts ─────────
  const clientStatusMap = useMemo(() => {
    const map: Record<string, { moving: number; stopped: number; workshop: number }> = {}
    for (const t of allTrucks) {
      const name = t.client_name
      if (!map[name]) map[name] = { moving: 0, stopped: 0, workshop: 0 }
      if (t.is_available === 0) {
        map[name].workshop++
      } else if (t.speed_kmh != null && t.speed_kmh > 0) {
        map[name].moving++
      } else {
        map[name].stopped++
      }
    }
    return map
  }, [allTrucks])

  // ── Client table totals ───────────────────────────────────
  const clientTotals = useMemo(() => {
    if (!data?.by_client?.length) return null
    const totalFleet = data.by_client.reduce((s, c) => s + c.total, 0)
    const totalInWorkshop = data.by_client.reduce((s, c) => s + c.in_workshop, 0)
    const totalAvailable = data.by_client.reduce((s, c) => s + c.available, 0)
    const pct = totalFleet > 0 ? Math.round((totalAvailable / totalFleet) * 10000) / 100 : 0
    const allCounts = Object.values(clientStatusMap)
    const totalMoving = allCounts.reduce((s, c) => s + c.moving, 0)
    const totalStopped = allCounts.reduce((s, c) => s + c.stopped, 0)
    return { totalFleet, totalInWorkshop, totalAvailable, totalMoving, totalStopped, pct }
  }, [data?.by_client, clientStatusMap])

  // ── Sorted history for chart (oldest → newest) ────────────
  const chartHistory = useMemo(
    () => [...historyData].sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date)),
    [historyData],
  )

  // ── Avg days in workshop ──────────────────────────────────
  const avgDays = data?.kpis?.avg_days_in_workshop ?? 0

  // ── GPS truck counts ──────────────────────────────────────
  const geoCount = useMemo(
    () => allTrucks.filter((t) => t.current_lat != null && t.current_lon != null).length,
    [allTrucks],
  )

  // ── Error state ───────────────────────────────────────────
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6">
        <p className="font-semibold text-lg">Failed to load dashboard</p>
        <p className="text-sm mt-2">{error}</p>
      </div>
    )
  }

  // ── Loading skeleton ──────────────────────────────────────
  if (isLoading && !data) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Command Center</h1>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <SkeletonCard count={6} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
          <div className="lg:col-span-3 bg-white rounded-xl p-5 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-48 mb-4" />
            <div className="h-[320px] bg-gray-100 rounded" />
          </div>
          <div className="lg:col-span-2 bg-white rounded-xl p-5 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-40 mb-4" />
            <div className="h-[320px] bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    )
  }

  if (!data) return null

  // ── Availability bar colour helper ────────────────────────
  const barColor = (pct: number) => {
    if (pct >= 90) return 'bg-green-500'
    if (pct >= 70) return 'bg-amber-500'
    return 'bg-red-500'
  }

  // ═════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════

  return (
    <div>
      {/* ═══ HEADER ════════════════════════════════════════════ */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E2A3A]">Command Center</h1>
          <p className="text-sm text-gray-500">Real-time fleet monitoring and workshop management</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Live
        </div>
      </div>

      {/* ═══ SECTION A — KPI CARDS ═══════════════════════════ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          title="TOTAL FLEET"
          value={data.kpis.total_fleet}
          subtitle="Registered assets"
          icon={<TruckIcon size={22} className="text-blue-600" />}
          iconBg="bg-blue-50"
        />
        <KPICard
          title="IN WORKSHOP"
          value={data.kpis.in_workshop}
          subtitle="Under maintenance"
          icon={<Wrench size={22} className="text-amber-600" />}
          iconBg="bg-amber-50"
          highlight={data.kpis.in_workshop > 0}
        />
        <KPICard
          title="ACTIVE FLEET"
          value={data.kpis.active_fleet}
          subtitle="On the road"
          icon={<CheckCircle size={22} className="text-green-600" />}
          iconBg="bg-green-50"
        />
        <KPICard
          title="READY TO RELEASE"
          value={data.kpis.ready_for_release}
          subtitle="Awaiting collection"
          icon={<Flag size={22} className="text-teal-600" />}
          iconBg="bg-teal-50"
          highlight={data.kpis.ready_for_release > 0}
        />
        <KPICard
          title="AVAILABILITY"
          value={data.kpis.availability_pct + '%'}
          subtitle="Fleet uptime rate"
          icon={<Activity size={22} className="text-purple-600" />}
          iconBg="bg-purple-50"
          valueColor={getAvailabilityColor(data.kpis.availability_pct)}
        />
        <KPICard
          title="AVG WORKSHOP DAYS"
          value={avgDays}
          subtitle="Mean turnaround"
          icon={<Wrench size={22} className="text-rose-600" />}
          iconBg="bg-rose-50"
          valueColor={avgDays > 7 ? 'text-red-600' : avgDays > 3 ? 'text-amber-600' : 'text-green-600'}
        />
      </div>

      {/* ═══ SECTION B — MAP + CHART ═══════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
        {/* ── LEFT: Availability Trend Line Chart ──────────── */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800">
            30-Day Availability Trend
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            Fleet availability percentage over time
          </p>

          {chartHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis
                  dataKey="snapshot_date"
                  tickFormatter={(val: string) => {
                    try { return format(new Date(val), 'dd MMM') }
                    catch { return val }
                  }}
                  tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v: number) => v + '%'}
                  tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={false}
                  tickLine={false}
                />
                <RechartTooltip
                  formatter={(value: number) => [value + '%', 'Availability']}
                  labelFormatter={(label: string) => {
                    try { return format(new Date(label), 'dd MMM yyyy') }
                    catch { return label }
                  }}
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    fontSize: '12px',
                  }}
                />
                <ReferenceLine
                  y={85}
                  stroke="#EF4444"
                  strokeDasharray="5 5"
                  label={{
                    value: 'Target 85%',
                    position: 'insideTopRight',
                    fontSize: 11,
                    fill: '#EF4444',
                  }}
                />
                <Line
                  dataKey="availability_pct"
                  stroke="#3B82F6"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#3B82F6' }}
                  type="monotone"
                  activeDot={{ r: 5, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[320px] flex items-center justify-center text-gray-400 text-sm">
              No availability history data yet
            </div>
          )}
        </div>

        {/* ── RIGHT: Vehicle Position Map ──────────────────── */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <MapPin size={16} className="text-blue-500" />
                Vehicle Positions
              </h2>
              <p className="text-xs text-gray-500">
                {geoCount} vehicles with GPS data
              </p>
            </div>
          </div>
          <VehicleMap
            trucks={allTrucks}
            onTruckClick={openDetail}
            height="320px"
          />
        </div>
      </div>

      {/* ═══ SECTION B.5 — STATUS DONUT + CLIENT TABLE ════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
        {/* ── Left: Fleet Status Compact Donut ─────────────── */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-800">Fleet Status</h2>
          <p className="text-xs text-gray-500 mb-2">Distribution by workshop status</p>

          {data.by_status.length > 0 ? (
            <div className="relative">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={data.by_status}
                    dataKey="count"
                    nameKey="status"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                  >
                    {data.by_status.map((entry) => (
                      <Cell
                        key={entry.status}
                        fill={STATUS_COLORS[entry.status as WorkshopStatus] ?? '#6B7280'}
                      />
                    ))}
                  </Pie>
                  <RechartTooltip
                    formatter={(value: number, name: string) => [value + ' trucks', name]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Center label */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <span className="text-2xl font-bold text-gray-800">{data.kpis.total_fleet}</span>
                  <br />
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide">Total</span>
                </div>
              </div>
              {/* Legend below */}
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 -mt-2">
                {data.by_status.map((entry) => (
                  <span key={entry.status} className="flex items-center gap-1 text-[10px] text-gray-600">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: STATUS_COLORS[entry.status as WorkshopStatus] ?? '#6B7280' }}
                    />
                    {entry.status} ({entry.count})
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
              No status data available
            </div>
          )}
        </div>

        {/* ── Right: Client Availability Table ─────────────── */}
        {data.by_client.length > 0 && (
          <div className="lg:col-span-3 bg-white rounded-xl shadow-sm">
            <div className="px-5 pt-5 pb-3 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">
                Fleet Availability by Client
              </h2>
              <p className="text-xs text-gray-500">
                Real-time breakdown per client group
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-5 py-3">Client</th>
                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-3">Total</th>
                    <th className="text-center text-xs font-semibold text-green-600 uppercase tracking-wide px-3 py-3">Moving</th>
                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-3">Stopped</th>
                    <th className="text-center text-xs font-semibold text-amber-600 uppercase tracking-wide px-3 py-3">Workshop</th>
                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-3">Avail %</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-5 py-3 min-w-[120px]">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.by_client.map((client) => {
                    const counts = clientStatusMap[client.client_name] ?? { moving: 0, stopped: 0, workshop: 0 }
                    return (
                      <tr key={client.client_name} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-2.5 font-medium text-gray-800 text-sm">{client.client_name}</td>
                        <td className="px-3 py-2.5 text-center text-gray-700 tabular-nums text-sm">{client.total}</td>
                        <td className="px-3 py-2.5 text-center tabular-nums text-sm">
                          <span className={counts.moving > 0 ? 'text-green-600 font-semibold' : 'text-gray-400'}>{counts.moving}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center tabular-nums text-sm text-gray-600">{counts.stopped}</td>
                        <td className="px-3 py-2.5 text-center tabular-nums text-sm">
                          <span className={counts.workshop > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}>{counts.workshop}</span>
                        </td>
                        <td className={`px-3 py-2.5 text-center font-semibold tabular-nums text-sm ${getAvailabilityColor(client.availability_pct)}`}>
                          {client.availability_pct}%
                        </td>
                        <td className="px-5 py-2.5">
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`${barColor(client.availability_pct)} rounded-full h-1.5 transition-all duration-500`}
                              style={{ width: `${Math.min(client.availability_pct, 100)}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })}

                  {clientTotals && (
                    <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                      <td className="px-5 py-2.5 text-gray-800 text-sm">TOTAL</td>
                      <td className="px-3 py-2.5 text-center text-gray-800 tabular-nums text-sm">{clientTotals.totalFleet}</td>
                      <td className="px-3 py-2.5 text-center text-green-700 tabular-nums text-sm">{clientTotals.totalMoving}</td>
                      <td className="px-3 py-2.5 text-center text-gray-800 tabular-nums text-sm">{clientTotals.totalStopped}</td>
                      <td className="px-3 py-2.5 text-center text-amber-700 tabular-nums text-sm">{clientTotals.totalInWorkshop}</td>
                      <td className={`px-3 py-2.5 text-center tabular-nums text-sm ${getAvailabilityColor(clientTotals.pct)}`}>
                        {clientTotals.pct}%
                      </td>
                      <td className="px-5 py-2.5">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`${barColor(clientTotals.pct)} rounded-full h-1.5 transition-all duration-500`}
                            style={{ width: `${Math.min(clientTotals.pct, 100)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ═══ SECTION D — TRUCKS IN WORKSHOP TABLE ═══════════ */}
      <div className="mt-6">
        {data.kpis.in_workshop === 0 ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex items-center gap-4">
            <CheckCircle size={32} className="text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800 text-base">
                All trucks are currently active and on the road.
              </p>
              <p className="text-green-600 text-sm mt-0.5">
                No vehicles are currently in the workshop.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="px-5 pt-5 pb-3 border-b border-gray-100 flex items-center gap-3">
              <h2 className="text-base font-semibold text-gray-800">
                Trucks Currently In Workshop
              </h2>
              <span className="bg-amber-100 text-amber-800 text-xs font-semibold rounded-full px-2 py-0.5">
                {data.trucks_in_workshop.length}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    {['Fleet #', 'Client', 'Type', 'Make/Model', 'Workshop', 'Status', 'Days in Workshop', 'Required Jobs', 'Est. Release', 'Actions'].map(
                      (h) => (
                        <th
                          key={h}
                          className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.trucks_in_workshop.map((truck) => {
                    const days = calcDaysInWorkshop(truck.workshop_entry_date)
                    return (
                      <tr
                        key={truck.asset_id}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                          {truck.registration}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {truck.client_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {truck.asset_type}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {[normalizeMake(truck.make), truck.model].filter(Boolean).join(' ')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {truck.workshop_location || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={truck.workshop_status} size="sm" />
                        </td>
                        <td className={`px-4 py-3 text-sm tabular-nums ${getDaysColor(days)}`}>
                          {days !== null ? `${days} day${days !== 1 ? 's' : ''}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px]">
                          {truncate(truck.required_jobs, 45)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(truck.estimated_release_date)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openDetail(truck)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                              title="View details"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => openEdit(truck)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-amber-600 transition-colors"
                              title="Update status"
                            >
                              <Edit2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ═══ SECTION E — READY FOR RELEASE ALERT ════════════ */}
      {data.kpis.ready_for_release > 0 && data.ready_for_release.length > 0 && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mt-4">
          <div className="flex items-center gap-3 mb-3">
            <Bell size={20} className="text-teal-600 flex-shrink-0" />
            <p className="font-semibold text-teal-800 text-sm">
              {data.ready_for_release.length} truck{data.ready_for_release.length !== 1 ? 's' : ''} ready
              for release — awaiting client collection
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.ready_for_release.map((truck) => (
              <button
                key={truck.asset_id}
                onClick={() => openDetail(truck)}
                className="bg-teal-100 text-teal-800 text-xs font-medium px-3 py-1
                           rounded-full cursor-pointer hover:bg-teal-200 transition-colors"
              >
                {truck.registration}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ═══ MODALS ═════════════════════════════════════════ */}
      {modalMode === 'detail' && selectedTruck && (
        <TruckDetailModal
          truck={selectedTruck}
          onClose={closeModal}
          onEdit={() => setModalMode('edit')}
        />
      )}

      {modalMode === 'edit' && selectedTruck && (
        <StatusUpdateModal
          truck={selectedTruck}
          onClose={closeModal}
          onSuccess={async () => {
            await refresh()
            addToast('success', `Status updated for ${selectedTruck.registration}`)
            closeModal()
          }}
        />
      )}
    </div>
  )
}
